<template>
  <v-row class="assortment-groups">
    <v-col class="col-border-left pt-0 pb-0" :cols="6">
      <div class="col-padding">
        <h3 class="assortment-groups__input-heading">
          {{ assortmentGroupsLabel }}
        </h3>
        <v-select
          v-model="selectedCategories"
          :items="categories"
          multiple
          :placeholder="$t('workpackagePage.scope.filterByCategoryLabel')"
        />
        <v-sheet>
          <v-text-field
            v-model="assortmentSearch"
            append-inner-icon="mdi-magnify"
            :placeholder="$t('workpackagePage.scope.searchLabel')"
          />

          <div data-id-e2e="listAvailableAssortmentGroupsOld" class="list pa-3">
            <ProgressBar v-if="loading" :message="$t('general.loading')" />
            <template v-else>
              <span v-if="hasTemplateAvailable">{{
                `${$t('general.from')} ${$t('general.template')}`
              }}</span>
              <v-virtual-scroll
                v-if="templatesEnabled && !!workpackage"
                :key="uniqueId('selectedGroup')"
                :items="unselectedAgsSection.fromTemplate"
                :item-height="32"
                class="scroller split-scroller"
              >
                <template #default="{ item }">
                  <span
                    class="list-item"
                    :class="{ selectable: clickableAGSelectionsEnabled }"
                    @click="clickableAGSelectionsEnabled && selectAssortmentGroup(item)"
                  >
                    {{ item.text }}
                    <v-icon
                      size="14"
                      color="primary"
                      class="list-icon"
                      @click.stop="selectAssortmentGroup(item)"
                    >
                      mdi-plus-thick
                    </v-icon>
                  </span>
                </template>
              </v-virtual-scroll>
              <span v-if="hasTemplateAvailable" class="">{{ $t('general.additional') }}</span>
              <v-virtual-scroll
                :key="uniqueId('availableGroups')"
                :items="unselectedAgsSection.additional"
                :item-height="32"
                class="scroller"
              >
                <template #default="{ item }">
                  <span
                    class="list-item"
                    :class="{ selectable: clickableAGSelectionsEnabled }"
                    @click="clickableAGSelectionsEnabled && selectAssortmentGroup(item)"
                  >
                    {{ item.text }}
                    <v-icon
                      size="14"
                      color="primary"
                      class="list-icon"
                      @click.stop="selectAssortmentGroup(item)"
                    >
                      mdi-plus-thick
                    </v-icon>
                  </span>
                </template>
              </v-virtual-scroll>
            </template>
          </div>
        </v-sheet>
      </div>
    </v-col>
    <v-col class="margin-right col-border-left pt-0 pb-0" :cols="6">
      <div
        data-id-e2e="listSelectedAssortmentGroupsOld"
        class="col-padding d-flex flex-column justify-start"
      >
        <v-row no-gutters class="row-assortment-groups">
          <v-col :cols="7">
            <h3 class="assortment-groups__input-heading">
              {{ $t('workpackagePage.scope.selectedAssortmentGroupsLabel') }}
              - {{ selectedAssortmentGroups.length }}
            </h3>
          </v-col>
          <v-col class="align" :cols="5">
            <v-btn
              :disabled="!canEditWorkpackageScope || isImportingFromPlano"
              color="primary"
              variant="plain"
              @click="resetSelectedAssortmentGroups"
            >
              {{ $t('workpackagePage.scope.resetSelection') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-sheet class="list selected-list pa-3">
          <ProgressBar v-if="loading" :message="$t('general.loading')" />
          <template v-else>
            <template v-if="templatesEnabled && !!workpackage">
              <span>{{ `${$t('general.from')} ${$t('general.template')}` }}</span>
              <v-virtual-scroll
                :key="uniqueId('selectedGroup')"
                :items="selectedAGsSection.fromTemplate"
                :item-height="32"
                class="scroller split-scroller"
              >
                <template #default="{ item }">
                  <span
                    class="list-item"
                    :class="{ selectable: clickableAGSelectionsEnabled }"
                    @click="clickableAGSelectionsEnabled && deselectAssortmentGroup(item)"
                  >
                    {{ item.text }}
                    <v-icon
                      size="14"
                      color="primary"
                      class="list-icon"
                      @click.stop="deselectAssortmentGroup(item)"
                    >
                      mdi-close-circle
                    </v-icon>
                  </span>
                </template>
              </v-virtual-scroll>
            </template>
            <span v-if="hasTemplateAvailable">{{ $t('general.additional') }}</span>
            <v-virtual-scroll
              :key="uniqueId('selectedGroup')"
              :items="selectedAGsSection.additional"
              :item-height="32"
              class="scroller"
            >
              <template #default="{ item }">
                <span
                  class="list-item"
                  :class="{ selectable: clickableAGSelectionsEnabled }"
                  @click="clickableAGSelectionsEnabled && deselectAssortmentGroup(props.item)"
                >
                  {{ item.text }}
                  <v-icon
                    size="14"
                    color="primary"
                    class="list-icon"
                    @click.stop="deselectAssortmentGroup(item)"
                  >
                    mdi-close-circle
                  </v-icon>
                </span>
              </template>
            </v-virtual-scroll>
          </template>
        </v-sheet>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import {
  includes,
  filter,
  flatMap,
  uniq,
  uniqueId,
  orderBy,
  get,
  keyBy,
  sortBy,
  values,
  isEmpty,
  differenceBy,
  partition,
} from 'lodash';

import { mapState, mapActions } from 'pinia';
import AssortmentGroupTypes from '@enums/assortment-group-types';

import { useWorkpackagesStore } from '@/stores/workpackages';
import { useContextStore } from '@/stores/context';
import { useWorkpackageProductsStore } from '@/stores/workpackage-products';
import { useAssortmentGroupsStore } from '@/stores/assortment-groups';

import { useFeature } from '@/composables/use-feature';
import { usePermission } from '@/composables/use-permission';

export default {
  props: {
    snapshotDate: {
      type: String,
      required: true,
    },

    resetAssortmentGroups: {
      type: Boolean,
      required: false,
      default: true,
    },

    workpackage: {
      type: Object,
      required: false, // not required for template
      default: null,
    },
  },

  setup() {
    const { templatesEnabled } = useFeature();
    const { canEditWorkpackageScope } = usePermission();
    return {
      templatesEnabled,
      canEditWorkpackageScope,
    };
  },

  data: () => ({
    selectedCategories: [],
    assortmentSearch: null,
    uniqueId,
  }),

  computed: {
    ...mapState(useContextStore, ['clientConfig', 'getClientConfig']),
    ...mapState(useAssortmentGroupsStore, [
      'masterAssortmentGroups',
      'categoryToAssortmentGroupKeysMap',
      'loading',
    ]),
    ...mapState(useWorkpackagesStore, [
      'isImportingFromPlano',
      'templatesById',
      'proposedAssortmentGroups',
      'wpSelectedAssortmentGroups',
    ]),

    selectedAssortmentGroups() {
      if (isEmpty(this.masterAssortmentGroups)) return [];

      return this.wpSelectedAssortmentGroups.map(({ key }) =>
        get(this.masterAssortmentGroupsByKey, key)
      );
    },

    unselectedAssortmentGroups() {
      if (isEmpty(this.masterAssortmentGroups)) return [];

      return differenceBy(this.masterAssortmentGroups, this.wpSelectedAssortmentGroups, 'key');
    },

    clickableAGSelectionsEnabled() {
      // The entire AG option should be clickable by default
      return get(this.getClientConfig, 'features.uiComponents.clickableAGSelections', true);
    },

    categories() {
      if (!this.unselectedAssortmentGroups) {
        return [];
      }

      const filterType = get(
        this.clientConfig,
        'assortmentGroups.filterType',
        'associatedProductCategoryDescriptions'
      );

      // Retrieve a unique list of categories
      const categories = flatMap(this.unselectedAssortmentGroups, ag => ag[filterType]);
      return orderBy(uniq(categories), [category => category.toLocaleLowerCase()], 'asc');
    },

    assortmentGroupsLabel() {
      const labelKey = {
        [AssortmentGroupTypes.furniture]: 'availableAssortmentGroupsLabelFurniture',
        [AssortmentGroupTypes.nonFurniture]: 'availableAssortmentGroupsLabelNonFurniture',
      }[this.clientConfig.assortmentGroups.mainType];
      return this.$t(`workpackagePage.scope.${labelKey}`, {
        num: this.availableAssortmentGroups.length,
      });
    },

    availableAssortmentGroups() {
      let filteredAssortmentGroups;
      // First check if there is a category filter
      if (this.selectedCategories.length === 0) {
        filteredAssortmentGroups = this.unselectedAssortmentGroups;
      } else {
        const uniqueKeys = uniq(
          flatMap(this.selectedCategories, c => this.categoryToAssortmentGroupKeysMap[c])
        );
        filteredAssortmentGroups = this.unselectedAssortmentGroups.filter(item => {
          return includes(uniqueKeys, item.key);
        });
      }

      // Now that we have filtered by category (or not), search by text
      if (this.assortmentSearch) {
        filteredAssortmentGroups = filteredAssortmentGroups.filter(item => {
          return includes(item.text.toLocaleLowerCase(), this.assortmentSearch.toLocaleLowerCase());
        });
      }

      return orderBy(
        filteredAssortmentGroups,
        [assortmentGroup => get(assortmentGroup, 'text', '').toLocaleLowerCase()],
        'asc'
      );
    },

    masterAssortmentGroupsByKey() {
      if (!this.masterAssortmentGroups) return {};

      return keyBy(this.masterAssortmentGroups, 'key');
    },

    assortmentGroupsFromTemplate() {
      if (!this.workpackage) {
        return {
          staticAssortmentGroupKeys: new Set([]),
          assortmentGroupsSelectedFromTemplate: [],
        };
      }
      const assortmentGroupsSelectedFromTemplate = get(
        this.templatesById,
        [this.workpackage.templateId, 'assortmentGroups'],
        []
      );

      const staticAssortmentGroupKeys = new Set(
        assortmentGroupsSelectedFromTemplate.map(ag => ag.staticAssortmentGroupKey)
      );

      return {
        staticAssortmentGroupKeys,
        assortmentGroupsSelectedFromTemplate,
      };
    },

    unselectedAgsSection() {
      if (isEmpty(this.masterAssortmentGroups)) return [];
      return this.splitFromTemplateVsAdditionalAssortmentGroups(this.availableAssortmentGroups);
    },

    selectedAGsSection() {
      if (isEmpty(this.masterAssortmentGroups)) return [];
      return this.splitFromTemplateVsAdditionalAssortmentGroups(this.selectedAssortmentGroups);
    },

    hasTemplateAvailable() {
      return !!get(this.workpackage, 'templateId', null);
    },

    useOldVersionOfAGSelection() {
      return get(this.clientConfig, 'features.useOldVersionOfAGSelection');
    },

    deleteAGDataOnResetSelectionEnabled() {
      return get(
        this.getClientConfig,
        'features.uiComponents.deleteAGDataOnResetSelectionEnabled',
        false
      );
    },
  },

  watch: {
    proposedAssortmentGroups: {
      handler(proposals) {
        const { proposedAGs } = proposals;
        // You can receive duplicate keys here. These are necessary for workpackage but not for templates.
        // For a match to be valid, it must be in the mongo AG collection
        const matches = proposedAGs.reduce((acc, { key }) => {
          const matchedAg = this.masterAssortmentGroupsByKey[key];
          if (matchedAg) acc[key] = matchedAg;
          return acc;
        }, {});

        this.setSelectedAssortmentGroups(values(matches));
      },
    },
  },

  async created() {
    await this.fetchAssortmentGroups(this.snapshotDate);
  },

  methods: {
    ...mapActions(useAssortmentGroupsStore, ['fetchAssortmentGroups']),
    ...mapActions(useWorkpackagesStore, ['updateWorkpackage', 'updateSelectedAssortmentGroups']),
    ...mapActions(useWorkpackageProductsStore, ['deleteWorkpackageProducts']),

    setSelectedAssortmentGroups(ags) {
      // These are always both true for templates
      const assortmentGroupsForSave = ags.map(ag => ({
        ...ag,
        analysis: true,
        assortment: true,
      }));

      this.updateSelectedAssortmentGroups(assortmentGroupsForSave);
    },

    async resetSelectedAssortmentGroups() {
      // Unselect all assortment groups
      this.setSelectedAssortmentGroups([]);

      if (this.deleteAGDataOnResetSelectionEnabled) {
        // Remove imported stores and products
        const workpackageId = this.workpackage._id;
        await Promise.all([
          await this.deleteWorkpackageProducts({ workpackageId }),
          await this.updateWorkpackage({ workpackageId, updates: { stores: [] } }),
        ]);
      }
    },

    selectAssortmentGroup(assortmentGroup) {
      // if we're selecting from template or user is using old view
      // always assign AG to both analysis and assortment
      if (!this.workpackage || this.useOldVersionOfAGSelection) {
        assortmentGroup.analysis = true;
        assortmentGroup.assortment = true;
      }

      this.setSelectedAssortmentGroups([...this.wpSelectedAssortmentGroups, assortmentGroup]);
    },

    deselectAssortmentGroup(assortmentGroup) {
      const { key } = assortmentGroup;
      const filteredAgs = filter(this.wpSelectedAssortmentGroups, ag => ag.key !== key);
      this.setSelectedAssortmentGroups(filteredAgs);
    },

    splitFromTemplateVsAdditionalAssortmentGroups(fromList) {
      const { staticAssortmentGroupKeys } = this.assortmentGroupsFromTemplate;
      // Handles issues where the AG list and selected AGs can temporarily differ during auto-assignment
      fromList = fromList.filter(x => x);

      const [fromTemplate, additional] = partition(fromList, ag =>
        staticAssortmentGroupKeys.has(ag.staticAssortmentGroupKey)
      );

      return {
        fromTemplate: sortBy(fromTemplate, 'text'),
        additional: sortBy(additional, 'text'),
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.assortment-groups {
  height: 100%;
  &__input-heading {
    margin-bottom: 7px;
    font-size: 1.2rem;
  }

  :deep(.v-input__append-inner) {
    margin-top: 0 !important;
  }

  :deep(&.pa-3) {
    overflow: auto !important;
  }

  :deep(.v-select__selections) {
    flex-wrap: nowrap;
  }

  a {
    font-size: 1.2rem;
    font-weight: normal;
  }

  .col-padding {
    height: 100%;
  }
}

.v-chip {
  width: 100%;
  height: auto;
  border-radius: 2px;
}
.v-chip__content {
  display: block !important;
}
.v-chip__content.v-icon {
  float: right;
}

.list {
  /* assortments list*/
  height: 430px;
  overflow-y: auto;
}
.selected-list {
  /* selected assortments list*/
  height: 500px;
}

.row-assortment-groups {
  flex: 0 1 auto;
}

.scroller {
  overflow-y: scroll;
  height: 100%;
}

// hack to remove component required by scroller
:deep(.split-scroller) {
  height: min-content !important;
  & > .resize-observer {
    display: none !important;
  }
}

.list-icon {
  position: absolute;
  right: 7px;
  font-size: 20px;
}

.list-item:not(.selectable) {
  cursor: default;
}
</style>
